<template>
  <div class="produtos-page">
    <HomeHeader />

    <!-- Hero Section com Efeito Parallax -->
    <section class="hero-section">
      <div class="parallax-container">
        <div class="overlay"></div>
        <h1 class="hero-title">{{ t('products.heroTitle') }}</h1>
        <div class="hero-subtitle">{{ t('products.heroSubtitle') }}</div>
      </div>
    </section>

    <!-- Seção de Introdução -->
    <section class="intro-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="100">
          <div class="section-title">
            <span class="accent-line"></span>
            <h2>{{ t('products.introTitle') }}</h2>
            <span class="accent-line"></span>
          </div>
          <p class="intro-text">
            {{ t('products.introText') }}
          </p>
        </ScrollReveal>
      </div>
    </section>

    <!-- Seção de Categorias de Produtos -->
    <section class="products-categories">
      <div class="container">
        <div class="categories-grid">
          <ScrollReveal direction="left" :delay="200">
            <div class="category-card">
              <div class="category-icon">
                <i class="fas fa-box-open"></i>
              </div>
              <div class="category-content">
                <h3>{{ t('products.categories.productLine.title') }}</h3>
                <p>{{ t('products.categories.productLine.description') }}</p>
                <div class="category-features">
                  <span v-for="feature in t('products.categories.productLine.features')" :key="feature">{{ feature
                    }}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" :delay="300">
            <div class="category-card reverse">
              <div class="category-content">
                <h3>{{ t('products.categories.diverseMedicines.title') }}</h3>
                <p>{{ t('products.categories.diverseMedicines.description') }}</p>
                <div class="category-features">
                  <span v-for="feature in t('products.categories.diverseMedicines.features')" :key="feature">{{ feature
                    }}</span>
                </div>
              </div>
              <div class="category-icon">
                <i class="fas fa-pills"></i>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="left" :delay="400">
            <div class="category-card">
              <div class="category-icon">
                <i class="fas fa-capsules"></i>
              </div>
              <div class="category-content">
                <h3>{{ t('products.categories.oncologyMedicines.title') }}</h3>
                <p>{{ t('products.categories.oncologyMedicines.description') }}</p>
                <div class="category-features">
                  <span v-for="feature in t('products.categories.oncologyMedicines.features')" :key="feature">{{ feature
                    }}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" :delay="500">
            <div class="category-card reverse">
              <div class="category-content">
                <h3>{{ t('products.categories.hospitalMedicines.title') }}</h3>
                <p>{{ t('products.categories.hospitalMedicines.description') }}</p>
                <div class="category-features">
                  <span v-for="feature in t('products.categories.hospitalMedicines.features')" :key="feature">{{ feature
                    }}</span>
                </div>
              </div>
              <div class="category-icon">
                <i class="fas fa-hospital-alt"></i>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- Seção de Destaques -->
    <section class="featured-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="section-title light">
            <span class="accent-line"></span>
            <h2>{{ t('products.differentialsTitle') }}</h2>
            <span class="accent-line"></span>
          </div>
        </ScrollReveal>

        <div class="features-grid">
          <ScrollReveal direction="bottom" :delay="300">
            <div class="feature-item">
              <div class="feature-icon">
                <i class="fas fa-truck-loading"></i>
              </div>
              <h4>{{ t('products.differentials.logistics.title') }}</h4>
              <p>{{ t('products.differentials.logistics.description') }}</p>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="bottom" :delay="400">
            <div class="feature-item">
              <div class="feature-icon">
                <i class="fas fa-certificate"></i>
              </div>
              <h4>{{ t('products.differentials.certified.title') }}</h4>
              <p>{{ t('products.differentials.certified.description') }}</p>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="bottom" :delay="500">
            <div class="feature-item">
              <div class="feature-icon">
                <i class="fas fa-warehouse"></i>
              </div>
              <h4>{{ t('products.differentials.stock.title') }}</h4>
              <p>{{ t('products.differentials.stock.description') }}</p>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="bottom" :delay="600">
            <div class="feature-item">
              <div class="feature-icon">
                <i class="fas fa-handshake"></i>
              </div>
              <h4>{{ t('products.differentials.partnerships.title') }}</h4>
              <p>{{ t('products.differentials.partnerships.description') }}</p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <!-- Seção de Catálogo -->
    <section class="catalog-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="catalog-content">
            <div class="catalog-image">
              <i class="fas fa-file-pdf"></i>
            </div>
            <div class="catalog-text">
              <h3>{{ t('products.catalog.title') }}</h3>
              <p>{{ t('products.catalog.description') }}</p>
              <a href="/uni-hospitalar-produtos.pdf" target="_blank" class="catalog-button">
                <i class="fas fa-download"></i> {{ t('products.catalog.downloadButton') }}
              </a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <!-- Seção CTA -->
    <section class="cta-section">
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="cta-content">
            <h2>{{ t('products.cta.title') }}</h2>
            <p>{{ t('products.cta.description') }}</p>
            <div class="cta-buttons">
              <router-link to="/Contato" class="cta-button primary">
                <i class="fas fa-envelope"></i> {{ t('products.cta.contactButton') }}</router-link>
              <a href="/uni-hospitalar-produtos.pdf" target="_blank" class="cta-button secondary">
                <i class="fas fa-download"></i> {{ t('products.cta.catalogButton') }}
              </a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <HomeFooter />
  </div>
</template>

<script>
import HomeHeader from '@/components/HomeHeader.vue';
import HomeFooter from '@/components/HomeFooter.vue';
import { useProductsTranslation } from '@/composables/useProductsTranslation.js';

export default {
  components: {
    HomeHeader,
    HomeFooter
  },
  setup() {
    const { t, currentLanguage } = useProductsTranslation()

    return {
      t,
      currentLanguage
    }
  },
  data() {
    return {
      selectedLanguage: 'pt',
      sidebarOpen: false
    }
  },
  methods: {
    changeLanguage(event) {
      const value = typeof event === 'string' ? event : event.target.value;
      this.selectedLanguage = value;
      console.log(`Idioma selecionado: ${value}`);
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    }
  }
}
</script>

<style scoped>
/* Estilos Gerais */
.produtos-page {
  font-family: 'Matter', 'Segoe UI', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

section {
  padding: 80px 0;
}

/* Hero Section com Parallax */
.hero-section {
  position: relative;
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  border-bottom: 4px solid #931414;
}

.parallax-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/header-produtos.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(147, 20, 20, 0.7), rgba(0, 0, 0, 0.7));
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 2px;
  animation: fadeInUp 1.5s ease-out;
}

.hero-subtitle {
  font-size: 1.5rem;
  font-weight: 300;
  position: relative;
  z-index: 2;
  animation: fadeInUp 1.5s ease-out 0.3s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Seção de Introdução */
.intro-section {
  background-color: #f0f0f0;
  text-align: center;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
}

.section-title h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #931414;
  margin: 0 20px;
}

.section-title.light h2 {
  color: #ffffff;
}

.accent-line {
  height: 3px;
  width: 70px;
  background-color: #931414;
}

.section-title.light .accent-line {
  background-color: #ffffff;
}

.intro-text {
  font-size: 1.3rem;
  max-width: 800px;
  margin: 0 auto;
  color: #555;
  line-height: 1.8;
}

/* Seção de Categorias de Produtos */
.products-categories {
  background-color: #ffffff;
  position: relative;
  padding: 100px 0;
  overflow: hidden;
}

/* Símbolo em bordô sangrando pelo canto superior esquerdo */
.products-categories::after {
  content: '';
  position: absolute;
  top: -357px;
  left: -336px;
  width: 1050px;
  height: 1050px;
  background-image: url('@/assets/uni-simbolo-outline.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  mask-image: linear-gradient(135deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(135deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  pointer-events: none;
  opacity: 0.16;
  z-index: 0;
}

.products-categories::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(240, 240, 240, 0.07) 0px, rgba(240, 240, 240, 0.07) 1px, transparent 1px, transparent 14px);
  opacity: 0.5;
}

.categories-grid {
  display: flex;
  flex-direction: column;
  max-width: 1100px;
  margin: 0 auto;
  gap: 30px;
}

.categories-grid>div {
  opacity: 0;
  transition: all 0.8s ease-out;
}

.categories-grid>div:nth-child(odd) {
  transform: translateX(-50px);
  align-self: flex-start;
  margin-right: auto;
}

.categories-grid>div:nth-child(even) {
  transform: translateX(50px);
  align-self: flex-end;
  margin-left: auto;
}

.categories-grid>div.is-revealed {
  opacity: 1;
  transform: translateX(0);
}

.categories-grid>div:nth-child(1) .category-card,
.categories-grid>div:nth-child(3) .category-card {
  width: 100%;
}

.categories-grid>div:nth-child(2) .category-card,
.categories-grid>div:nth-child(4) .category-card {
  width: 100%;
}

.categories-grid>div:nth-child(2) .category-content h3::after,
.categories-grid>div:nth-child(4) .category-content h3::after {
  left: auto;
  right: 0;
}

.category-card {
  display: flex;
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  max-width: 800px;
}

.category-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.category-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 100px;
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  font-size: 2.5rem;
  padding: 20px;
}

.category-content {
  padding: 25px;
  flex: 1;
}

.category-content h3 {
  font-size: 1.4rem;
  color: #931414;
  margin-bottom: 12px;
  font-weight: 700;
  position: relative;
  padding-bottom: 12px;
}

.category-content h3::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 50px;
  height: 3px;
  background-color: #931414;
}

.category-content p {
  color: #696969;
  margin-bottom: 15px;
  line-height: 1.6;
  font-size: 0.95rem;
}

.category-features {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.category-features span {
  background-color: #f0f0f0;
  color: #931414;
  padding: 8px 15px;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.category-features span:hover {
  background-color: #931414;
  color: white;
  transform: translateY(-3px);
}

/* Seção de Destaques */
.featured-section {
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  position: relative;
  overflow: hidden;
}

/* Símbolo em branco centralizado, desvanecendo em círculo */
.featured-section::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1440px;
  height: 1440px;
  background-image: url('@/assets/uni-simbolo-outline-branco.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  mask-image: radial-gradient(circle, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.35) 55%, transparent 80%);
  -webkit-mask-image: radial-gradient(circle, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.35) 55%, transparent 80%);
  pointer-events: none;
  opacity: 0.12;
  z-index: 0;
}

.featured-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.08) 0px, rgba(255, 255, 255, 0.08) 1px, transparent 1px, transparent 14px);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 60px;
  position: relative;
  z-index: 1;
}

.feature-item {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  backdrop-filter: blur(5px);
  transition: transform 0.3s ease, background-color 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

.feature-item:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.2);
}

.feature-icon {
  font-size: 2.2rem;
  margin-bottom: 12px;
  color: rgba(255, 255, 255, 0.9);
  flex-shrink: 0;
}

.feature-item h4 {
  font-size: 1.2rem;
  margin-bottom: 10px;
  color: white;
  flex-shrink: 0;
}

.feature-item p {
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  flex-grow: 1;
  display: flex;
  align-items: center;
}

/* Seção de Catálogo */
.catalog-section {
  background-color: #f0f0f0;
  position: relative;
  overflow: hidden;
}

/* Símbolo em rubi sangrando pelo canto inferior direito */
.catalog-section::after {
  content: '';
  position: absolute;
  bottom: -399px;
  right: -357px;
  width: 1092px;
  height: 1092px;
  background-image: url('@/assets/uni-simbolo-outline-rubi.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  mask-image: linear-gradient(315deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(315deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 55%, transparent 85%);
  pointer-events: none;
  opacity: 0.15;
  z-index: 0;
}

.catalog-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(240, 240, 240, 0.07) 0px, rgba(240, 240, 240, 0.07) 1px, transparent 1px, transparent 14px);
  opacity: 0.5;
}

.catalog-content {
  display: flex;
  align-items: center;
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.catalog-image {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  font-size: 5rem;
  padding: 20px;
}

.catalog-text {
  padding: 40px;
  flex: 1;
}

.catalog-text h3 {
  font-size: 2rem;
  color: #470404;
  margin-bottom: 15px;
}

.catalog-text p {
  color: #696969;
  margin-bottom: 25px;
  font-size: 1.1rem;
}

/* .catalog-button está na regra global de glass, mas o fundo aqui
   (.catalog-content) é branco chapado, então precisa de opacidade
   alta pra não ficar sujo/desbotado */
.catalog-button {
  position: relative;
  overflow: hidden;
  display: inline-block;
  padding: 15px 30px;
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.32), rgba(71, 4, 4, 0.32)) !important;
  backdrop-filter: blur(20px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(200%) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(147, 20, 20, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.catalog-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.catalog-button:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(147, 20, 20, 0.4);
}

.catalog-button i {
  position: relative;
  margin-right: 10px;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
}

/* Seção CTA */
.cta-section {
  background: linear-gradient(135deg, #f0f0f0, #f0f0f0);
  text-align: center;
  padding: 100px 0;
}

.cta-content {
  max-width: 700px;
  margin: 0 auto;
}

.cta-content h2 {
  font-size: 2.5rem;
  color: #470404;
  margin-bottom: 20px;
  font-weight: 700;
}

.cta-content p {
  font-size: 1.2rem;
  color: #696969;
  margin-bottom: 30px;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

/* .cta-button e .cta-button.primary estão na regra global de glass,
   mas o fundo aqui (.cta-section) é claro chapado, então precisa de
   opacidade alta pra não ficar sujo/desbotado. .secondary é um botão
   outline (fundo branco/borda) que a regra global quebraria também. */
.cta-button {
  position: relative;
  overflow: hidden;
  display: inline-block;
  padding: 15px 40px;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
}

.cta-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.cta-button i {
  position: relative;
  margin-right: 10px;
}

.cta-button.primary {
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.32), rgba(71, 4, 4, 0.32)) !important;
  backdrop-filter: blur(20px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(200%) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
  box-shadow: 0 10px 20px rgba(147, 20, 20, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.cta-button.primary i {
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
}

.cta-button.secondary {
  background: rgba(255, 255, 255, 0.88) !important;
  backdrop-filter: blur(20px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(200%) !important;
  color: #931414 !important;
  -webkit-text-fill-color: #931414 !important;
  border: 2px solid #931414 !important;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.cta-button.secondary i {
  background-image: none !important;
  -webkit-background-clip: initial !important;
  background-clip: initial !important;
  -webkit-text-fill-color: #931414 !important;
  color: #931414 !important;
}

.cta-button.primary:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(147, 20, 20, 0.4);
}

.cta-button.secondary:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  background: rgba(249, 249, 249, 0.97) !important;
}

.feature-item:hover .feature-icon {
  transform: scale(1.2);
  transition: transform 0.3s ease;
}

.cta-button.secondary:hover {
  color: #470404;
  border-color: #470404;
}

.feature-item:nth-child(1) {
  border-bottom: 3px solid rgba(71, 4, 4, 0.5);
}

.feature-item:nth-child(2) {
  border-bottom: 3px solid rgba(85, 150, 255, 0.5);
}

.feature-item:nth-child(3) {
  border-bottom: 3px solid rgba(85, 255, 157, 0.5);
}

.feature-item:nth-child(4) {
  border-bottom: 3px solid rgba(255, 204, 85, 0.5);
}

/* Responsividade */
@media (max-width: 1200px) {
  .container {
    padding: 0 40px;
  }
  
  .categories-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1139px) {
  .features-grid {
    grid-template-columns: 1fr 1fr;
    row-gap: 80px;
  }
}

@media (max-width: 992px) {
  .hero-title {
    font-size: 3rem;
  }

  .hero-subtitle {
    font-size: 1.3rem;
  }

  section {
    padding: 60px 0;
  }

  .category-card {
    flex-direction: column;
    width: 100%;
    max-width: 100%;
  }

  .category-card.reverse {
    flex-direction: column;
    width: 100%;
    max-width: 100%;
  }

  .category-card.reverse .category-icon {
    order: -1;
  }

  .category-icon {
    min-width: 100%;
    padding: 30px 0;
  }

  .categories-grid>div {
    width: 100% !important;
    max-width: 100% !important;
    align-self: center !important;
  }

  .catalog-content {
    flex-direction: column;
  }

  .catalog-image {
    min-width: 100%;
    height: 150px;
  }

  .features-grid {
    margin-bottom: 20px;
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 60vh;
    min-height: 400px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .section-title h2 {
    font-size: 2rem;
  }

  .intro-text {
    font-size: 1.1rem;
  }

  .cta-content h2 {
    font-size: 2rem;
  }

  .catalog-text h3 {
    font-size: 1.8rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .categories-grid>div {
    align-self: center !important;
  }

  .categories-grid>div:nth-child(1) .category-card,
  .categories-grid>div:nth-child(3) .category-card,
  .categories-grid>div:nth-child(2) .category-card,
  .categories-grid>div:nth-child(4) .category-card {
    width: 100%;
  }

  .category-card {
    flex-direction: column;
  }

  .category-icon {
    min-width: 100%;
    padding: 15px 0;
  }

  .cta-button.primary,
  .cta-button.secondary {
    width: 50%;
  }
}

@media (max-width: 576px) {
  .container {
    padding: 0 20px;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .section-title h2 {
    font-size: 1.8rem;
  }

  .accent-line {
    width: 40px;
  }

  .cta-button {
    padding: 12px 30px;
    font-size: 1rem;
    width: 50%;
    margin: 0 auto;
    display: block;
  }

  .cta-buttons {
    flex-direction: column;
    gap: 15px;
  }

  .category-features {
    justify-content: center;
  }
}

/* Animações adicionais */
@keyframes float {
  0% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }

  100% {
    transform: translateY(0px);
  }
}

.category-icon i,
.feature-icon i,
.catalog-image i {
  animation: float 3s ease-in-out infinite;
}

.category-card:hover .category-icon {
  transform: scale(1.05);
  transition: transform 0.3s ease;
}

/* Animação para o botão de catálogo */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(147, 20, 20, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0);
  }
}

.catalog-button:hover,
.cta-button.primary:hover {
  animation: pulse 1.5s infinite;
}

/* Ajustes para dispositivos móveis */
@media (max-width: 480px) {
  .hero-title {
    font-size: 1.8rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .category-content h3 {
    font-size: 1.4rem;
  }

  .feature-item {
    padding: 20px;
  }

  .feature-icon {
    font-size: 2rem;
  }

  .feature-item h4 {
    font-size: 1.2rem;
  }

  .catalog-text {
    padding: 20px;
  }

  .catalog-text h3 {
    font-size: 1.5rem;
  }

  .catalog-text p {
    font-size: 1rem;
  }

  .catalog-button {
    padding: 10px;
    font-size: 0.7rem;
  }

  .cta-button.primary,
  .cta-button.secondary {
    width: 80%;
  }
}

</style>
